import type ReactPlayer from 'react-player'
import screenfull from 'screenfull'

import type { MediaReducerMiddleware } from './types'
import { Action } from './types'

type ToggleFullscreenMiddleware = (ref: React.RefObject<HTMLDivElement>) => MediaReducerMiddleware

export const fullscreenMiddleware: ToggleFullscreenMiddleware =
  (ref) => () => (next) => (action) => {
    if (action.type === Action.TOGGLE_FULLSCREEN) {
      if (screenfull.isEnabled && ref.current) {
        screenfull.toggle(ref.current)
      }
    }

    return next(action)
  }

type ReactPlayerMiddleware = (player: React.RefObject<ReactPlayer>) => MediaReducerMiddleware

export const reactPlayerMiddleware: ReactPlayerMiddleware =
  (player) => (store) => (next) => (action) => {
    if (action.type === Action.SEEK_TO) {
      const state = store.getState()
      player.current?.seekTo(action.payload + state.start, 'seconds')
    }

    if (action.type === Action.SET_PROGRESS) {
      const state = store.getState()
      const { start } = state
      const { playedSeconds, duration } = action.payload

      if (playedSeconds < start) {
        player.current?.seekTo(start, 'seconds')
      }

      if (playedSeconds >= duration + start) {
        player.current?.seekTo(start, 'seconds')
      }
    }
    return next(action)
  }

type ReactPlayerAnalyticMiddleware = (
  onPlay?: (playSecond: number, duration: number) => void,
  onPause?: (playSecond: number, duration: number) => void,
  onSeek?: (seek: { seekFrom: number; seekTo: number; duration: number }) => void,
) => MediaReducerMiddleware

export const reactPlayerAnalyticMiddleware: ReactPlayerAnalyticMiddleware =
  (onPlay, onPause, onSeek) => (store) => (next) => (action) => {
    if (action.type === Action.PLAY) {
      const state = store.getState()
      const { playedSeconds, playing, duration } = state
      if (!playing && duration > 0) {
        onPlay?.(playedSeconds || 0, duration)
      }
    }
    if (action.type === Action.PAUSE) {
      const state = store.getState()
      const { playedSeconds, duration, playing } = state
      if (playing && duration > 0) {
        onPause?.(playedSeconds || 0, duration)
      }
    }

    if (action.type === Action.SET_PROGRESS) {
      const state = store.getState()
      const { start, played, loop, playing } = state
      const { playedSeconds, duration } = action.payload

      if (playedSeconds >= duration + start && duration > 0 && loop) {
        if (playing) {
          onPause?.(duration, duration)
        }
        //:TODO: Math.floor(played || 0) or 0 ??
        onPlay?.(Math.floor(played || 0), duration)
      }
    }

    if (action.type === Action.TOGGLE_PLAY) {
      const state = store.getState()
      const { playing, playedSeconds, duration } = state
      if (!playing && duration > 0) {
        onPlay?.(playedSeconds || 0, duration)
      }
      if (playing && duration > 0) {
        onPause?.(playedSeconds || 0, duration)
      }
    }

    if (action.type === Action.SEEK_TO) {
      const state = store.getState()
      const { duration, seekStartPosition, playedSeconds = 0 } = state
      // Use seekStartPosition (saved when seeking started) as the source position
      const seekFrom = seekStartPosition ?? playedSeconds
      onSeek?.({ seekFrom, seekTo: action.payload, duration })
    }

    return next(action)
  }
